/**
 * 화면모드(라이트/다크) · 글자크기 — 모바일 전역 스위치
 *
 * <html data-theme="dark" data-fs="l|xl"> 한 곳만 건드린다. 라이트·보통은 속성 없음(CSS `:root` 가 기본값).
 * 실제 색/배율은 CSS 가 해석한다 (abstracts/_tokens.scss, base/_fontsize.scss).
 *
 * 유저 선호는 **기기(localStorage)** 에 저장한다 — 서버에 보내지 않는다(기기 간에 이어지지 않는다).
 * 로드 시엔 <head> 안 부트 스크립트(App\Common\ThemeBoot)가 같은 키를 읽어 속성을 붙이므로 깜빡임이 없다.
 * 이 모듈은 그 뒤의 런타임 토글(설정화면·전체메뉴 밝기/가 버튼)을 담당한다.
 *
 * ── 동작 범위 ─────────────────────────────────────────────
 * 모바일 전 페이지 오픈 — 구 config/theme.php 화이트리스트(dark_ready/font_ready)와
 * <html data-dark-ready/data-font-ready/data-fs-preview/data-debug> 게이트는 폐지됐다.
 * 이 모듈은 main.js 에 번들돼 악덕/PC 등에서도 로드되므로, body[data-page-type='Mobile'] 로 모바일만 동작한다.
 * (⚠ 토큰화 안 된 레거시 화면도 이제 다크/확대가 걸린다 — 깨지면 개별 대응.)
 *
 * 사용법
 *   $j.F.Mobile.Theme.getPref()          // 유저가 고른 값 (스위치가 보여줄 값)
 *   $j.F.Mobile.Theme.getTheme()         // 화면에 실제 적용된 값
 *   $j.F.Mobile.Theme.setTheme('dark')
 *   $j.F.Mobile.Theme.setFontSize('l')
 */
const THEMES = ['light', 'dark', 'auto']; // 선호값(pref). [0]=기본. 'auto'=시간대 자동. data-theme 에는 dark 만 들어간다.
const SIZES = ['m', 'l', 'xl'];   // 보통 / 크게 / 아주크게

// localStorage 키 — ⚠ 경계키: PHP 쪽 App\Common\ThemeBoot::KEYS['JB'] 와 값이 같아야 한다 (개명은 양쪽 동시에).
const K_THEME = 'm:ui:theme';
const K_FS    = 'm:ui:fontSize';

export default class Theme {
    constructor() {
        // data-theme/data-fs 는 <head> 의 부트 스크립트가 이미 붙였다(App\Common\ThemeBoot) — 여기서 apply() 하지 않는다.
        // 런타임 토글(setTheme/setFontSize)만 apply() 로 <html> 을 갱신한다.
        // main.js 는 악덕/PC 등에서도 로드되므로, DOM 준비 후 모바일(body[data-page-type=Mobile])에서만 부수작업을 한다.
        $(() => {
            if (!this.enabled) return;
            this.swapAssets();      // 테마별 이미지 스왑
            this.syncMobiscroll();  // 서드파티 피커(mobiscroll)를 앱 테마에 맞춤
        });
    }

    // 이 페이지가 모바일(테마 대상)인가 — 악덕은 body[data-page-type='Devil']. main.js 공유라 여기서 가른다.
    get enabled() { return !!document.body && document.body.dataset.pageType === 'Mobile'; }

    // 로그인 전에는 화면모드·글자크기를 걸지 않는다 — 부트 스크립트(ThemeBoot::MEMBER_ONLY)와 같은 기준.
    get loggedIn() { return typeof MEMBER !== 'undefined' && !!MEMBER && !!MEMBER.num; }

    /** 유저가 고른 값 (기기 저장값) */
    getPref() { return this._pick(THEMES, $j.Common.Store.local.get(K_THEME)); }
    getFontSizePref() { return this._pick(SIZES, $j.Common.Store.local.get(K_FS)); }

    /** 화면에 실제 적용된 값 */
    getTheme() {
        return this._pick(THEMES, document.documentElement.getAttribute('data-theme'));
    }

    setTheme(theme) {
        if (!this.loggedIn) return this.getPref();
        this._save(K_THEME, this._pick(THEMES, theme));
        this.apply();
        this.swapAssets();
        this.syncMobiscroll();
        return this.getPref();
    }

    /**
     * 서드파티 날짜/휠 피커(mobiscroll v5)를 앱 테마에 맞춘다.
     * mobiscroll 기본 themeVariant='auto' 는 OS(prefers-color-scheme)를 따르므로 우리 data-theme 토글과 어긋난다.
     * 전역 기본값을 앱 테마로 고정하면, 이후 생성되는 피커가 라이브러리 네이티브 다크(mbsc-*-dark)로 뜬다.
     * (모바일에서만 호출된다 — 생성자가 body[data-page-type=Mobile] 로 가드하므로 관리자/PC/악덕 피커는 안 건드린다)
     *
     * 전역 setOptions 이므로 '누가 마지막에 부르는가' 가 곧 결과다.
     * 판단은 이 메서드 한 곳에서만 하고, 다른 초기화 코드는 themeVariant 를 직접 넣지 않는다.
     */
    syncMobiscroll() {
        if (typeof mobiscroll === 'undefined' || !mobiscroll.setOptions) return;
        const dark = this.getTheme() === 'dark';
        mobiscroll.setOptions({ themeVariant: dark ? 'dark' : 'light' });
    }

    /**
     * 테마별 이미지 교체 — 같은 <img> 의 src 만 바꾼다(레이아웃/위치 변화 0).
     * 대상: data-src-dark 를 가진 img (예: 햄버거 아이콘 — 회색라인+빨간점 2톤이라 CSS 필터로 못 다룸).
     * 라이트 원본 src 는 최초 1회 data-src-light 로 보관해 되돌릴 수 있게 한다.
     */
    swapAssets() {
        const dark = this.getTheme() === 'dark';
        document.querySelectorAll('img[data-src-dark]').forEach((img) => {
            if (!img.dataset.srcLight) img.dataset.srcLight = img.getAttribute('src');
            img.setAttribute('src', dark ? img.dataset.srcDark : img.dataset.srcLight);
        });
    }

    setFontSize(size) {
        if (!this.loggedIn) return this.getFontSizePref();
        this._save(K_FS, this._pick(SIZES, size));
        this.apply();
        return this.getFontSizePref();
    }

    /**
     * <html data-theme/data-fs> 갱신 — 런타임 토글 시.
     * 라이트·보통은 **속성을 지운다** — CSS 가 `:root`(=라이트·보통)를 기본값으로 깔고 있어,
     * 부트 스크립트도 다크·확대일 때만 속성을 붙인다. 여기서도 같은 규칙을 지켜야 대칭이 맞는다.
     */
    apply() {
        const el = document.documentElement;
        const theme = this._resolveTheme(this.getPref()); // 'auto' 는 시간대로 풀어 light|dark 로
        const fs = this.getFontSizePref();
        if (theme === 'dark') el.setAttribute('data-theme', 'dark');
        else el.removeAttribute('data-theme');
        if (fs !== 'm') el.setAttribute('data-fs', fs);
        else el.removeAttribute('data-fs');
    }

    /**
     * 선호값(light|dark|auto)을 실제 data-theme(light|dark)으로 푼다.
     * 'auto' = 시간대 자동 — 19:00~06:59 다크, 07:00~18:59 라이트. (부트 스크립트 ThemeBoot 와 경계 일치)
     */
    _resolveTheme(pref) {
        const p = this._pick(THEMES, pref);
        if (p !== 'auto') return p;
        const h = new Date().getHours();
        return (h >= 19 || h < 7) ? 'dark' : 'light';
    }

    /**
     * 상단 +/- 버튼 핸들러 — 3단계(보통↔크게↔아주크게)를 오르내린다.
     * @param n +1(확대) / -1(축소)
     */
    stepFontSize(n) {
        if (!this.enabled || !this.loggedIn) return; // 모바일이 아니면(악덕 등)·로그인 전이면 무시
        const cur = this.getFontSizePref();
        let i = SIZES.indexOf(cur);
        if (i < 0) i = 0;
        i = Math.min(SIZES.length - 1, Math.max(0, i + n));
        this.setFontSize(SIZES[i]);
        // 구 줌과 동일하게 화면 한가운데 박스(#errmgshowbox, SystemUi 가 모바일 페이지에 주입)로 잠깐 보여주고 사라진다.
        const labels = { m: '보통', l: '크게', xl: '아주크게' };
        const $box = $('#errmgshowbox');
        if ($box.length) $box.text(labels[SIZES[i]]).stop(true, true).show().delay(800).fadeOut(400);
    }

    /**
     * 레거시 페이지별 줌 쿠키(ZoomSet_<path>, 60~170%) 청소.
     *
     * 쿠키값 → 3단계 이전은 **끝났다** — 그 결과가 회원설정(app_config.fontsize)에 남았고,
     * 지금은 부트 스크립트가 그 값을 기기(localStorage)로 옮겨온다(ThemeBoot 이관 블록).
     * 즉 여기서 쿠키를 다시 스캔하면 이미 확정된 값을 추정치로 덮어쓸 뿐이라, 청소만 남겼다.
     * (쿠키는 경로별로 흩어져 있어 현재 페이지에서 보이는 것만 지워지므로 매 로드마다 쓸어낸다.)
     */
    migrateLegacyZoom() {
        this._clearLegacyZoomCookies();
    }

    /**
     * 레거시 줌 쿠키(ZoomSet_*)를 만료시켜 삭제한다.
     * 원본(구 Zoom.js.setRatio)이 path 없이 심어 기본 path=문서 디렉토리라, 삭제하려면 그 path 로 만료해야 한다.
     * document.cookie 는 path 속성을 노출하지 않으므로, 현재 경로의 모든 상위 디렉토리 + 루트를 후보 path 로
     * 순회하며 각각 만료시킨다(+path 미지정도 시도). 현재 페이지에서 보이는(=path 가 현재 경로의 상위인) 쿠키가 대상.
     */
    _clearLegacyZoomCookies() {
        const cookie = $j.Common.Store.cookie;
        const names = cookie.names().filter((k) => k.indexOf('ZoomSet_') === 0);
        if (!names.length) return;

        const paths = new Set(['/', location.pathname]);
        const parts = location.pathname.split('/');
        for (let i = 1; i < parts.length; i++) paths.add(parts.slice(0, i + 1).join('/') || '/');

        names.forEach((name) => {
            cookie.remove(name, null);                     // path 미지정(현재 경로 기준)
            paths.forEach((p) => cookie.remove(name, p));  // 상위 디렉토리 + 루트 후보 순회
        });
    }

    // 기기(localStorage)에 저장. 서버로 보내지 않는다 — 기기 설정이라 계정·기기 간에 이어지지 않는다.
    _save(key, value) {
        $j.Common.Store.local.set(key, value);
    }

    _pick(list, value) {
        return list.indexOf(value) > -1 ? value : list[0];
    }
}
